import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Card, Field, IconButton, Input, Note, Progress, StandaloneScreen, Sym,
} from '../../components/app2';
import authService from '../../utils/authService';
import AlertService from '../../utils/alertService';

/**
 * Cambio de contraseña obligatorio (rediseño jul-2026).
 *
 * Pantalla suelta: ocurre ANTES de entrar a la app, así que no lleva el shell.
 * Se monta sobre StandaloneScreen (wrapper `.as2` + fuentes + tema) en vez del
 * Header antiguo de pestañas, que era lo último que lo usaba.
 *
 * La lógica no cambia: mismo servicio (authService.changePassword), mismas
 * validaciones, mismo medidor de fortaleza y misma salida (redirige al inicio
 * del rol o cierra sesión).
 */

const REQUISITOS = [
  { test: (v) => (v || '').length >= 8, label: 'Al menos 8 caracteres' },
  { test: (v) => /[A-Z]/.test(v || ''), label: 'Una letra mayúscula' },
  { test: (v) => /[a-z]/.test(v || ''), label: 'Una letra minúscula' },
  { test: (v) => /\d/.test(v || ''), label: 'Un número' },
  { test: (v) => /[!@#$%^&*(),.?":{}|<>]/.test(v || ''), label: 'Un carácter especial' },
];

const NIVELES = [
  { max: 1, label: 'Muy débil', color: 'var(--danger)' },
  { max: 2, label: 'Débil', color: 'var(--warn)' },
  { max: 3, label: 'Regular', color: 'var(--warn)' },
  { max: 4, label: 'Buena', color: 'var(--brand)' },
  { max: 5, label: 'Muy fuerte', color: 'var(--ok)' },
];

const MandatoryPasswordChange = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
    isValid: false,
  });

  // Redirect if user doesn't need to change password (solo al montar).
  // `navigate` de react-router 6.0.2 es inestable; usarlo como dep provoca loop
  // de render. Deps vacías = chequeo único al montar. Ver LoginForm para el detalle.
  useEffect(() => {
    const currentUser = authService?.getCurrentUser();
    if (!currentUser?.must_change_password) {
      const redirectRoute = authService?.getUserRedirectRoute() || '/app/system-analytics';
      navigate(redirectRoute, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculatePasswordStrength = (password) => {
    const feedback = REQUISITOS.filter((r) => !r.test(password)).map((r) => r.label);
    const score = REQUISITOS.length - feedback.length;

    setPasswordStrength({
      score,
      feedback,
      isValid: score >= 4 && (password?.length || 0) >= 6,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear field-specific errors
    if (errors?.[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[name];
        return newErrors;
      });
    }

    // Clear general errors
    if (errors?.general) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.general;
        return newErrors;
      });
    }

    // Update password strength in real-time for new password
    if (name === 'newPassword') {
      calculatePasswordStrength(value);
    }

    // Validate password confirmation in real-time
    if (name === 'confirmNewPassword' || (name === 'newPassword' && formData?.confirmNewPassword)) {
      const newPass = name === 'newPassword' ? value : formData?.newPassword;
      const confirmPass = name === 'confirmNewPassword' ? value : formData?.confirmNewPassword;

      if (confirmPass && newPass !== confirmPass) {
        setErrors(prev => ({ ...prev, confirmNewPassword: 'Las contraseñas no coinciden' }));
      } else if (errors?.confirmNewPassword) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors?.confirmNewPassword;
          return newErrors;
        });
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Current password validation
    if (!formData?.currentPassword?.trim()) {
      newErrors.currentPassword = 'La contraseña actual es requerida';
    }

    // New password validation
    if (!formData?.newPassword?.trim()) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
    } else if (formData?.newPassword?.length < 6) {
      newErrors.newPassword = 'La nueva contraseña debe tener al menos 6 caracteres';
    }

    // Confirm password validation
    if (!formData?.confirmNewPassword?.trim()) {
      newErrors.confirmNewPassword = 'La confirmación de contraseña es requerida';
    } else if (formData?.newPassword !== formData?.confirmNewPassword) {
      newErrors.confirmNewPassword = 'Las contraseñas no coinciden';
    }

    // Same password check
    if (formData?.currentPassword && formData?.newPassword &&
        formData?.currentPassword === formData?.newPassword) {
      newErrors.newPassword = 'La nueva contraseña debe ser diferente a la actual';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const result = await authService?.changePassword(
        formData?.currentPassword,
        formData?.newPassword
      );

      if (result?.success) {
        // Get appropriate redirect route based on user role
        const redirectRoute = authService?.getUserRedirectRoute() || '/app/system-analytics';

        await AlertService?.success(
          'Contraseña cambiada exitosamente',
          'Redirigiendo al sistema...'
        );

        navigate(redirectRoute, { replace: true });
      } else {
        setErrors({
          general: result?.error || 'Error al cambiar contraseña. Inténtelo nuevamente.',
        });
      }
    } catch (error) {
      console.error('Password change submission error:', error);
      setErrors({
        general: 'Error de conexión. Verifique su conexión a internet e inténtelo nuevamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev?.[field] }));
  };

  const handleLogout = async () => {
    const confirmed = await AlertService?.confirm(
      '¿Está seguro que desea cerrar sesión?',
      'Deberá cambiar su contraseña la próxima vez que inicie sesión.'
    );

    if (confirmed) {
      authService?.logout();
    }
  };

  const nivel = NIVELES.find((n) => passwordStrength.score <= n.max) || NIVELES[NIVELES.length - 1];

  const ojo = (campo, visible) => (
    <IconButton
      icon={visible ? 'visibility_off' : 'visibility'}
      title={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      type="button"
      disabled={isLoading}
      onClick={() => togglePasswordVisibility(campo)}
      style={{
        position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
        width: 34, height: 34, border: 'none', background: 'none',
      }}
    />
  );

  return (
    <StandaloneScreen maxWidth={560} title="Cambio de contraseña obligatorio — PaySlip">
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <span
            style={{
              width: 42, height: 42, borderRadius: 12, flexShrink: 0,
              background: 'var(--warn-soft)', border: '1px solid var(--warn)',
              color: 'var(--warn)', display: 'grid', placeItems: 'center',
            }}
          >
            <Sym name="lock_reset" size={22} />
          </span>
          <div style={{ minWidth: 0 }}>
            <h1 className="a2-card-title" style={{ fontSize: '1.3rem' }}>
              Cambia tu contraseña
            </h1>
            <p className="a2-card-sub">
              Por seguridad, la contraseña temporal debe cambiarse antes de entrar
            </p>
          </div>
        </div>

        {errors?.general ? (
          <Note tone="danger" style={{ marginBottom: 16 }}>{errors.general}</Note>
        ) : null}

        <form onSubmit={handleSubmit}>
          <Field
            label="Contraseña actual *"
            htmlFor="mpc-current"
            error={errors?.currentPassword}
            style={{ marginBottom: 16 }}
          >
            <div style={{ position: 'relative' }}>
              <Input
                id="mpc-current"
                name="currentPassword"
                type={showPasswords?.current ? 'text' : 'password'}
                value={formData?.currentPassword}
                onChange={handleInputChange}
                placeholder="La contraseña que te enviamos por correo"
                autoComplete="current-password"
                disabled={isLoading}
                style={{ paddingRight: 46 }}
              />
              {ojo('current', showPasswords?.current)}
            </div>
          </Field>

          <Field
            label="Nueva contraseña *"
            htmlFor="mpc-new"
            error={errors?.newPassword}
            style={{ marginBottom: 16 }}
          >
            <div style={{ position: 'relative' }}>
              <Input
                id="mpc-new"
                name="newPassword"
                type={showPasswords?.new ? 'text' : 'password'}
                value={formData?.newPassword}
                onChange={handleInputChange}
                placeholder="Elige una nueva contraseña"
                autoComplete="new-password"
                disabled={isLoading}
                style={{ paddingRight: 46 }}
              />
              {ojo('new', showPasswords?.new)}
            </div>

            {formData?.newPassword ? (
              <div style={{ marginTop: 10 }}>
                <div
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 8, marginBottom: 6,
                  }}
                >
                  <span className="a2-tcell-muted">Fortaleza de la contraseña</span>
                  <span style={{ font: "700 .78rem 'Hanken Grotesk'", color: nivel.color }}>
                    {nivel.label}
                  </span>
                </div>
                <Progress
                  value={(passwordStrength.score / REQUISITOS.length) * 100}
                  color={nivel.color}
                  label={`Fortaleza: ${nivel.label}`}
                />
              </div>
            ) : null}
          </Field>

          <Field
            label="Confirmar nueva contraseña *"
            htmlFor="mpc-confirm"
            error={errors?.confirmNewPassword}
            style={{ marginBottom: 16 }}
          >
            <div style={{ position: 'relative' }}>
              <Input
                id="mpc-confirm"
                name="confirmNewPassword"
                type={showPasswords?.confirm ? 'text' : 'password'}
                value={formData?.confirmNewPassword}
                onChange={handleInputChange}
                placeholder="Repite la nueva contraseña"
                autoComplete="new-password"
                disabled={isLoading}
                style={{ paddingRight: 46 }}
              />
              {ojo('confirm', showPasswords?.confirm)}
            </div>
          </Field>

          {/* Requisitos: se van marcando conforme se cumplen */}
          <div
            className="a2-surface-2"
            style={{ borderRadius: 12, border: '1px solid var(--line-2)', padding: '14px 16px' }}
          >
            <div className="a2-tcell-muted" style={{ marginBottom: 8 }}>
              Requisitos de la contraseña
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 8 }}>
              {REQUISITOS.map((r) => {
                const ok = r.test(formData?.newPassword);
                return (
                  <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Sym
                      name={ok ? 'check_circle' : 'radio_button_unchecked'}
                      size={17}
                      style={{ color: ok ? 'var(--ok)' : 'var(--faint)', flexShrink: 0 }}
                    />
                    <span
                      style={{
                        font: "500 .84rem 'Hanken Grotesk'",
                        color: ok ? 'var(--heading)' : 'var(--muted)',
                      }}
                    >
                      {r.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
            <Button
              type="submit"
              icon="lock"
              loading={isLoading}
              disabled={isLoading || !passwordStrength?.isValid}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              Cambiar contraseña
            </Button>

            <Button
              type="button"
              variant="outline"
              icon="logout"
              onClick={handleLogout}
              disabled={isLoading}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              Cerrar sesión
            </Button>
          </div>
        </form>
      </Card>

      <p
        style={{
          font: "500 .82rem 'Hanken Grotesk'", color: 'var(--muted)',
          textAlign: 'center', margin: '16px 0 0',
        }}
      >
        Es una medida de seguridad obligatoria: no podrás usar PaySlip hasta cambiarla.
      </p>
    </StandaloneScreen>
  );
};

export default MandatoryPasswordChange;
